const AUTH_COPY = {
  en: {
    shell: {
      productMeta: 'Artava Systems application',
      websiteShort: 'artavasystems.com',
      footerEmail: 'hello@artavasystems.com',
      footerWebsite: 'artavasystems.com',
      footerLinkedIn: 'LinkedIn',
      focusAreasLabel: 'Artava focus areas',
      expectedFlowLabel: 'Expected flow',
      languageLabel: 'Language',
      variants: {
        signin: {
          badge: 'Custom software for industrial systems',
          title: 'Smarti, by Artava Systems.',
          body: 'Custom software for complex industrial systems.',
          metrics: [
            { label: 'Who we are', value: 'Artava Systems from Torino, Italy.' },
            { label: 'What we do', value: 'Embedded software, industrial apps, and data platforms.' },
            { label: 'Contact us', value: 'hello@artavasystems.com' },
          ],
          cards: [
            {
              eyebrow: 'Need new software?',
              title: 'Need software for a complex system?',
              text: 'Artava builds custom industrial software around real technical constraints.',
            },
          ],
          ctas: [
            { label: 'Request a Technical Assessment', href: 'https://www.artavasystems.com/en/contact', tone: 'primary' },
            { label: 'Visit Artava Systems', href: 'https://www.artavasystems.com/en', tone: 'secondary' },
          ],
          links: [
            { label: 'About Artava', href: 'https://www.artavasystems.com/en/about' },
            { label: 'Contact', href: 'https://www.artavasystems.com/en/contact' },
            { label: 'Website', href: 'https://www.artavasystems.com/en' },
          ],
        },
        register: {
          badge: 'Guided workspace onboarding',
          title: 'Request Smarti access with the owner and next step already clear.',
          body: 'Strong company apps make access requests feel governed and easy to route. Users should see the Artava identity, the Smarti product context, and the approval step before they submit.',
          metrics: [
            { label: 'Request flow', value: 'Submit, review, approve, activate' },
            { label: 'Access type', value: 'Workspace request for production teams' },
            { label: 'Operational fit', value: 'Designed for industrial and technical teams' },
          ],
          cards: [
            {
              eyebrow: 'Common B2B norm',
              title: 'Transparent approval steps',
              text: 'Registration works better when users know what happens after they submit and who reviews the request.',
            },
            {
              eyebrow: 'Company signal',
              title: 'Support and ownership stay visible',
              text: 'Contact details, company identity, and product purpose belong on onboarding pages so the app feels accountable from the first interaction.',
            },
          ],
          steps: [
            {
              title: 'Share identity and use case',
              text: 'Capture the person, organization, and deployment context behind the request.',
            },
            {
              title: 'Workspace review',
              text: 'The request is reviewed for fit, licensing, and rollout intent before activation.',
            },
            {
              title: 'Activate the workspace',
              text: 'Approved users receive the next-step email and finish setup inside Smarti.',
            },
          ],
          links: [
            { label: 'About Artava Systems', href: 'https://www.artavasystems.com/en/about' },
            { label: 'Contact Artava Systems', href: 'https://www.artavasystems.com/en/contact' },
            { label: 'Company website', href: 'https://www.artavasystems.com/en' },
          ],
        },
      },
      focusAreas: [
        'Embedded Software',
        'Industrial Applications',
        'Engineering Data Platforms',
        'Systems Integration',
      ],
    },
    login: {
      eyebrow: 'Artava Systems access',
      title: 'Sign in to Smarti',
      invitationNotice: 'This invitation is linked to {{username}}. Sign in with the approved account to continue.',
      sectionTitle: 'Workspace credentials',
      sectionText: 'Use the approved Smarti account to enter the workspace.',
      username: 'Username',
      usernamePlaceholder: 'Enter your username',
      password: 'Password',
      passwordPlaceholder: 'Enter your password',
      login: 'Login',
      signingIn: 'Signing in...',
      requestAccessLead: "Don't have access yet?",
      requestAccessAction: 'Request a Smarti account',
      loginError: 'Login failed.',
    },
    register: {
      eyebrow: 'Smarti onboarding',
      title: 'Request Smarti access',
      subtitle: 'Request access to the Smarti workspace for dashboards, approvals, and engineering reviews.',
      choosePathTitle: 'Choose the account path',
      choosePathText: 'Most company applications separate normal workspace requests from privileged setup. That makes onboarding clearer for users and safer for the platform owner.',
      workspaceUserTitle: 'Workspace user',
      workspaceUserText: 'Request access for dashboards, analytics, and daily Smarti workflows. Approval is handled by an administrator.',
      workspaceAdminTitle: 'Workspace admin',
      workspaceAdminText: 'Reserved for controlled setup and internal ownership. This path requires the admin secret key.',
      username: 'Username',
      usernamePlaceholder: 'Enter your username',
      accessNotice: 'Access requests are reviewed by an administrator. Approved users receive an activation email.',
      accessDetailsTitle: 'Access request details',
      accessDetailsText: 'A short request is enough here.',
      requestType: 'Request type',
      requestTypes: {
        demo: 'Demo evaluation',
        pilot: 'Pilot rollout',
        production: 'Production deployment',
      },
      email: 'Email',
      fullName: 'Full name',
      fullNamePlaceholder: 'Your full name',
      company: 'Company',
      companyPlaceholder: 'Artava Systems or customer organization',
      department: 'Department',
      departmentPlaceholder: 'Quality, production, engineering...',
      plant: 'Plant or site',
      plantPlaceholder: 'Plant, lab, or operating site',
      team: 'Team',
      teamPlaceholder: 'Team name',
      project: 'Project or line',
      projectPlaceholder: 'Program, line, or project',
      reason: 'Note',
      reasonPlaceholder: 'Add a short note about who needs access or what the account is for',
      adminNotice: 'Admin setup is meant for controlled Smarti ownership and managed deployments. Use this path only if you have the assigned secret key.',
      adminSetupTitle: 'Admin setup',
      adminSetupText: 'Privileged account creation should feel deliberate. The form stays short, the password policy stays visible, and the control point stays obvious.',
      password: 'Password',
      passwordPlaceholder: 'Use 10+ characters with upper, lower, number, and symbol',
      adminSecretKey: 'Admin secret key',
      adminSecretKeyPlaceholder: 'Enter the admin secret key',
      submitRequest: 'Send access request',
      createAdmin: 'Create admin workspace',
      submitting: 'Submitting...',
      alreadyHaveAccessLead: 'Already have access?',
      goToLogin: 'Go to login',
      successUser: 'Access request sent successfully.',
      successAdmin: 'User registered successfully.',
      error: 'Error during registration. Please try again.',
    },
  },
  it: {
    shell: {
      productMeta: 'Applicazione Artava Systems',
      websiteShort: 'artavasystems.com',
      footerEmail: 'hello@artavasystems.com',
      footerWebsite: 'artavasystems.com',
      footerLinkedIn: 'LinkedIn',
      focusAreasLabel: 'Aree Artava',
      expectedFlowLabel: 'Flusso previsto',
      languageLabel: 'Lingua',
      variants: {
        signin: {
          badge: 'Software custom per sistemi industriali',
          title: 'Smarti, di Artava Systems.',
          body: 'Software custom per sistemi industriali complessi.',
          metrics: [
            { label: 'Chi siamo', value: 'Artava Systems da Torino, Italia.' },
            { label: 'Cosa facciamo', value: 'Software embedded, app industriali e piattaforme dati.' },
            { label: 'Contattaci', value: 'hello@artavasystems.com' },
          ],
          cards: [
            {
              eyebrow: 'Ti serve nuovo software?',
              title: 'Ti serve software per un sistema complesso?',
              text: 'Artava sviluppa software industriale custom su vincoli tecnici reali.',
            },
          ],
          ctas: [
            { label: 'Richiedi una valutazione tecnica', href: 'https://www.artavasystems.com/en/contact', tone: 'primary' },
            { label: 'Visita Artava Systems', href: 'https://www.artavasystems.com/en', tone: 'secondary' },
          ],
          links: [
            { label: 'Chi e Artava', href: 'https://www.artavasystems.com/en/about' },
            { label: 'Contatti', href: 'https://www.artavasystems.com/en/contact' },
            { label: 'Sito web', href: 'https://www.artavasystems.com/en' },
          ],
        },
        register: {
          badge: 'Onboarding workspace guidato',
          title: 'Richiedi accesso a Smarti con proprieta e passo successivo gia chiari.',
          body: 'Le buone app aziendali fanno percepire la richiesta di accesso come un processo governato e facile da instradare. L\'utente deve vedere identita Artava, contesto Smarti e passaggio di approvazione prima di inviare.',
          metrics: [
            { label: 'Flusso richiesta', value: 'Invio, revisione, approvazione, attivazione' },
            { label: 'Tipo accesso', value: 'Richiesta workspace per team di produzione' },
            { label: 'Contesto', value: 'Pensato per team industriali e tecnici' },
          ],
          cards: [
            {
              eyebrow: 'Norma B2B',
              title: 'Passi di approvazione trasparenti',
              text: 'La registrazione funziona meglio quando l\'utente sa cosa succede dopo l\'invio e chi revisiona la richiesta.',
            },
            {
              eyebrow: 'Segnale aziendale',
              title: 'Supporto e proprieta sempre visibili',
              text: 'Contatti, identita aziendale e scopo del prodotto devono restare visibili nelle pagine di onboarding.',
            },
          ],
          steps: [
            {
              title: 'Condividi identita e caso d\'uso',
              text: 'Raccogli persona, organizzazione e contesto di deployment dietro la richiesta.',
            },
            {
              title: 'Revisione workspace',
              text: 'La richiesta viene revisionata per fit, licenza e rollout prima dell\'attivazione.',
            },
            {
              title: 'Attiva il workspace',
              text: 'Gli utenti approvati ricevono l\'email successiva e completano il setup in Smarti.',
            },
          ],
          links: [
            { label: 'Chi e Artava Systems', href: 'https://www.artavasystems.com/en/about' },
            { label: 'Contatta Artava Systems', href: 'https://www.artavasystems.com/en/contact' },
            { label: 'Sito aziendale', href: 'https://www.artavasystems.com/en' },
          ],
        },
      },
      focusAreas: [
        'Software Embedded',
        'Applicazioni Industriali',
        'Piattaforme Dati Ingegneristiche',
        'System Integration',
      ],
    },
    login: {
      eyebrow: 'Accesso Artava Systems',
      title: 'Accedi a Smarti',
      invitationNotice: 'Questo invito e collegato a {{username}}. Accedi con l\'account approvato per continuare.',
      sectionTitle: 'Credenziali workspace',
      sectionText: 'Usa l\'account Smarti approvato per entrare nel workspace.',
      username: 'Nome utente',
      usernamePlaceholder: 'Inserisci il tuo nome utente',
      password: 'Password',
      passwordPlaceholder: 'Inserisci la tua password',
      login: 'Accedi',
      signingIn: 'Accesso in corso...',
      requestAccessLead: 'Non hai ancora accesso?',
      requestAccessAction: 'Richiedi un account Smarti',
      loginError: 'Accesso non riuscito.',
    },
    register: {
      eyebrow: 'Onboarding Smarti',
      title: 'Richiedi accesso a Smarti',
      subtitle: 'Richiedi accesso allo workspace Smarti per dashboard, approvazioni e review engineering.',
      choosePathTitle: 'Scegli il percorso account',
      choosePathText: 'Molte applicazioni aziendali separano le richieste workspace normali dal setup privilegiato. Cosi l\'onboarding e piu chiaro per l\'utente e piu sicuro per il proprietario della piattaforma.',
      workspaceUserTitle: 'Utente workspace',
      workspaceUserText: 'Richiedi accesso per dashboard, analytics e flussi Smarti quotidiani. L\'approvazione e gestita da un amministratore.',
      workspaceAdminTitle: 'Admin workspace',
      workspaceAdminText: 'Riservato a setup controllati e ownership interna. Questo percorso richiede la chiave segreta admin.',
      username: 'Nome utente',
      usernamePlaceholder: 'Inserisci il tuo nome utente',
      accessNotice: 'Le richieste di accesso vengono revisionate da un amministratore. Gli utenti approvati ricevono una email di attivazione.',
      accessDetailsTitle: 'Dettagli richiesta accesso',
      accessDetailsText: 'Qui basta una richiesta corta.',
      requestType: 'Tipo richiesta',
      requestTypes: {
        demo: 'Valutazione demo',
        pilot: 'Rollout pilota',
        production: 'Deployment produzione',
      },
      email: 'Email',
      fullName: 'Nome completo',
      fullNamePlaceholder: 'Il tuo nome completo',
      company: 'Azienda',
      companyPlaceholder: 'Artava Systems o organizzazione cliente',
      department: 'Reparto',
      departmentPlaceholder: 'Qualita, produzione, engineering...',
      plant: 'Plant o sito',
      plantPlaceholder: 'Plant, laboratorio o sito operativo',
      team: 'Team',
      teamPlaceholder: 'Nome team',
      project: 'Progetto o linea',
      projectPlaceholder: 'Programma, linea o progetto',
      reason: 'Nota',
      reasonPlaceholder: 'Aggiungi una breve nota su chi ha bisogno dell\'accesso o a cosa serve l\'account',
      adminNotice: 'Il setup admin e pensato per ownership Smarti controllata e deployment gestiti. Usa questo percorso solo se hai la chiave segreta assegnata.',
      adminSetupTitle: 'Setup admin',
      adminSetupText: 'La creazione di account privilegiati deve sembrare deliberata. La form resta breve, la policy password resta visibile e il punto di controllo resta evidente.',
      password: 'Password',
      passwordPlaceholder: 'Usa almeno 10 caratteri con maiuscola, minuscola, numero e simbolo',
      adminSecretKey: 'Chiave segreta admin',
      adminSecretKeyPlaceholder: 'Inserisci la chiave segreta admin',
      submitRequest: 'Invia richiesta accesso',
      createAdmin: 'Crea workspace admin',
      submitting: 'Invio in corso...',
      alreadyHaveAccessLead: 'Hai gia accesso?',
      goToLogin: 'Vai al login',
      successUser: 'Richiesta di accesso inviata con successo.',
      successAdmin: 'Utente registrato con successo.',
      error: 'Errore durante la registrazione. Riprova.',
    },
  },
};

export function getAuthCopy(language) {
  return String(language || '').toLowerCase().startsWith('it') ? AUTH_COPY.it : AUTH_COPY.en;
}
